/**
 * Storage Service - API client for Google Drive-like storage.
 *
 * This service handles:
 * - Folder hierarchy management
 * - File operations with folder support
 * - Move/copy/trash operations
 * - Search functionality
 * - Breadcrumb navigation
 * - Storage quota
 */

import type {
  PickerImportRequest,
  PickerImportResponse,
} from '@/types/google-drive-types';
import ApiService, { type ApiResponse } from './api-services';

// =============================================================================
// Types
// =============================================================================

export type StorageNodeType = 'file' | 'folder';
export type StorageProvider = 'supabase' | 'google_drive';
export type StorageObjectStatus =
  | 'active'
  | 'read_only'
  | 'deleting'
  | 'deleted';
type StorageObjectSource = 'upload' | 'chat' | 'research' | 'google_drive';

export interface ConnectedProvider {
  id: number;
  provider: StorageProvider;
  provider_email: string | null;
  provider_name: string | null;
  is_active: boolean;
  created_at: string | null;
}

export interface StorageNode {
  id: number;
  user_id: string;
  type: StorageNodeType;
  name: string;
  path: string;
  parent_id: number | null;
  provider: StorageProvider;
  provider_id: string | null;
  size_bytes: number | null;
  content_type: string | null;
  file_extension: string | null;
  object_key: string | null;
  status: StorageObjectStatus | null;
  is_analyzed: boolean;
  source_session_id: string | null;
  download_url: string | null;
  /**
   * Managed-node binding (Spisy milestone, #6320). `null` for ordinary nodes.
   * When set, this node belongs to the reserved `Lexomat / Klienti / <klient> /
   * <spis>` subtree auto-synced from clients -> cases, and the Drive UI
   * highlights it as a synced case folder. One of:
   * `lexomat_root` | `klienti_root` | `client` | `case`.
   */
  managed_kind?: string | null;
  managed_client_id?: number | null;
  managed_case_id?: number | null;
  created_at: string | null;
  updated_at: string | null;
  trashed_at: string | null;
}

export interface SessionFilesSummary {
  session_id: string;
  file_count: number;
  total_bytes: number;
  total_mb: number;
}

interface NodeListResponse {
  nodes: StorageNode[];
  total: number;
  page: number;
  limit: number;
  has_more: boolean;
  current_path: string;
}

export interface BreadcrumbItem {
  id: number | null;
  name: string;
  path: string;
}

interface BreadcrumbsResponse {
  items: BreadcrumbItem[];
}

interface CreateFolderRequest {
  name: string;
  parent_id?: number | null;
}

interface RenameNodeRequest {
  new_name: string;
}

interface MoveNodesRequest {
  node_ids: number[];
  target_folder_id?: number | null;
}

/** Machine-readable skip cause — localize toasts off this, never off the
 * English `reason` text (backend `SkippedReasonCode`, #7455). */
export type MoveSkippedReasonCode = 'not_found' | 'name_conflict' | 'error';

/** A node the backend could not move (mirrors backend `SkippedNode`). */
export interface MoveSkippedNode {
  node_id: number;
  name: string;
  reason: string;
  reason_code: MoveSkippedReasonCode;
}

/** A node auto-renamed on a destination name collision (backend `RenamedNode`). */
interface MoveRenamedNode {
  node_id: number;
  old_name: string;
  new_name: string;
}

/** `POST /storage/move` payload — `status: 'partial'` when `skipped` is
 * non-empty (#7342/#7455). Callers consume it via `moveNodes` inference. */
interface MoveNodesResponseData {
  nodes: StorageNode[];
  renamed: MoveRenamedNode[];
  skipped: MoveSkippedNode[];
}

interface CopyNodesRequest {
  node_ids: number[];
  target_folder_id?: number | null;
}

interface TrashNodesRequest {
  node_ids: number[];
  permanent?: boolean;
}

interface RestoreNodesRequest {
  node_ids: number[];
}

interface CreateFileRecordRequest {
  storage_key: string;
  original_filename: string;
  size_bytes: number;
  mime_type: string;
  folder_id?: number | null;
  source?: StorageObjectSource;
  source_session_id?: string | null;
  checksum?: string | null;
}

interface DuplicateCheckRequest {
  checksum: string;
  filename: string;
  folder_id?: number | null;
}

interface DuplicateCheckResponse {
  has_content_duplicate: boolean;
  has_name_conflict: boolean;
  existing_file: StorageNode | null;
  conflicting_file: StorageNode | null;
}

interface UploadUrlRequest {
  filename: string;
  size_bytes: number;
  content_type: string;
  folder_id?: number | null;
}

interface UploadUrlResponse {
  upload_url: string;
  storage_key: string;
  folder_id: number | null;
}

interface SearchRequest {
  q: string;
  folder_id?: number | null;
  type?: StorageNodeType | null;
  limit?: number;
}

/** Response of GET /storage/index/status (whole-storage indexing, #6319). */
interface StorageIndexStatus {
  /** Owned, active file-nodes that have never had an indexing attempt. */
  unindexed_file_count: number;
  /**
   * Owned, active file-nodes with a completed index run (#6348). The composer
   * gates the `whole_storage` chip auto-select on this being > 0 so a user with
   * nothing indexed isn't handed an empty retrieval scope.
   */
  indexed_file_count: number;
}

/** Response of POST /storage/index/backfill — re-invoke until remaining === 0. */
interface StorageBackfillResult {
  selected: number;
  enqueued: number;
  skipped: number;
  remaining: number;
}

// =============================================================================
// Storage Service Class
// =============================================================================

export class StorageService extends ApiService {
  private static instance: StorageService;

  constructor() {
    super(''); // Empty base - methods use full paths like /storage/nodes
  }

  public static getInstance(): StorageService {
    if (!StorageService.instance) {
      StorageService.instance = new StorageService();
    }
    return StorageService.instance;
  }

  // ===========================================================================
  // Node Listing
  // ===========================================================================

  /**
   * List trashed items across all folders.
   * Returns nodes with original names (trashed suffix stripped).
   */
  async listTrashedNodes(
    page = 1,
    limit = 50
  ): Promise<ApiResponse<NodeListResponse>> {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    return this.getData(`/storage/trash/items?${params.toString()}`);
  }

  /**
   * List nodes (files and folders) in a directory.
   */
  async listNodes(
    folderId?: number | null,
    page = 1,
    limit = 50,
    includeTrashed = false
  ): Promise<ApiResponse<NodeListResponse>> {
    const params = new URLSearchParams();
    if (folderId !== undefined && folderId !== null) {
      params.set('folder_id', String(folderId));
    }
    params.set('page', String(page));
    params.set('limit', String(limit));
    params.set('include_trashed', String(includeTrashed));

    return this.getData(`/storage/nodes?${params.toString()}`);
  }

  /**
   * Get a single node by ID.
   */
  getNode(nodeId: number): ApiResponse<StorageNode> {
    return this.getData(`/storage/nodes/${nodeId}`);
  }

  /**
   * Get breadcrumb navigation for a folder.
   */
  async getBreadcrumbs(
    folderId?: number | null
  ): Promise<ApiResponse<BreadcrumbsResponse>> {
    const params = new URLSearchParams();
    if (folderId !== undefined && folderId !== null) {
      params.set('folder_id', String(folderId));
    }
    return this.getData(`/storage/breadcrumbs?${params.toString()}`);
  }

  // ===========================================================================
  // Folder Operations
  // ===========================================================================

  /**
   * Create a new folder.
   */
  async createFolder(
    data: CreateFolderRequest
  ): Promise<ApiResponse<StorageNode>> {
    return this.postData('/storage/folders', data);
  }

  /**
   * Get existing folder by name or create if it doesn't exist.
   * Useful for auto-save features where a specific folder is needed.
   */
  async getOrCreateFolder(
    name: string,
    parentId?: number | null
  ): Promise<ApiResponse<StorageNode>> {
    return this.postData('/storage/folders/get-or-create', {
      name,
      parent_id: parentId,
    });
  }

  // ===========================================================================
  // File Operations
  // ===========================================================================

  /**
   * Check for duplicate files before upload.
   * Returns info about content duplicates and name conflicts.
   */
  async checkDuplicate(
    data: DuplicateCheckRequest
  ): Promise<ApiResponse<DuplicateCheckResponse>> {
    return this.postData('/storage/check-duplicate', data);
  }

  /**
   * Get a signed upload URL.
   */
  async getUploadUrl(
    data: UploadUrlRequest
  ): Promise<ApiResponse<UploadUrlResponse>> {
    return this.postData('/storage/upload-url', data);
  }

  /**
   * Create a file record after successful upload.
   */
  async createFileRecord(
    data: CreateFileRecordRequest
  ): Promise<ApiResponse<StorageNode>> {
    return this.postData('/storage/files', data);
  }

  /**
   * Get a signed download URL for a file.
   */
  async getDownloadUrl(nodeId: number): ApiResponse<{ download_url: string }> {
    return this.getData(`/storage/files/${nodeId}/download`);
  }

  /**
   * Get all items recursively for download (preserves folder structure).
   */
  async getRecursiveDownloadItems(nodeIds: number[]): Promise<
    ApiResponse<{
      items: Array<{
        id: number;
        name: string;
        type: 'file' | 'folder';
        path: string;
        download_url: string | null;
      }>;
    }>
  > {
    return this.postData('/storage/download-recursive', { node_ids: nodeIds });
  }

  // ===========================================================================
  // Move/Copy/Rename/Delete Operations
  // ===========================================================================

  /**
   * Move nodes to a new location.
   *
   * Returns `status: 'partial'` with `data.skipped` populated when some
   * nodes could not be moved, and `data.renamed` for nodes auto-renamed on
   * a destination name collision (#7342/#7455).
   */
  async moveNodes(
    data: MoveNodesRequest
  ): Promise<ApiResponse<MoveNodesResponseData>> {
    return this.postData('/storage/move', data);
  }

  /**
   * Copy nodes to a new location.
   */
  async copyNodes(
    data: CopyNodesRequest
  ): Promise<ApiResponse<{ nodes: StorageNode[] }>> {
    return this.postData('/storage/copy', data);
  }

  /**
   * Rename a node.
   */
  async renameNode(
    nodeId: number,
    data: RenameNodeRequest
  ): Promise<ApiResponse<StorageNode>> {
    return this.patchData(`/storage/nodes/${nodeId}`, data);
  }

  /**
   * Trash or permanently delete nodes.
   */
  async trashNodes(
    data: TrashNodesRequest
  ): Promise<ApiResponse<{ count: number }>> {
    return this.postData('/storage/trash', data);
  }

  /**
   * Restore trashed nodes.
   */
  async restoreNodes(
    data: RestoreNodesRequest
  ): Promise<ApiResponse<{ nodes: StorageNode[] }>> {
    return this.postData('/storage/restore', data);
  }

  /**
   * Empty the trash (permanently delete all trashed items).
   */
  async emptyTrash(): Promise<ApiResponse<{ count: number }>> {
    return this.deleteData('/storage/trash', null);
  }

  // ===========================================================================
  // Search
  // ===========================================================================

  /**
   * Search for nodes by name.
   */
  async searchNodes(
    params: SearchRequest
  ): Promise<ApiResponse<{ nodes: StorageNode[] }>> {
    const urlParams = new URLSearchParams();
    urlParams.set('q', params.q);
    if (params.folder_id !== undefined && params.folder_id !== null) {
      urlParams.set('folder_id', String(params.folder_id));
    }
    if (params.type) {
      urlParams.set('type', params.type);
    }
    if (params.limit) {
      urlParams.set('limit', String(params.limit));
    }

    return this.getData(`/storage/search?${urlParams.toString()}`);
  }

  // ===========================================================================
  // Bulk Download
  // ===========================================================================

  /**
   * Get all files with download URLs for bulk download.
   */
  async getAllFilesForDownload(): Promise<
    ApiResponse<{ files: StorageNode[] }>
  > {
    return this.getData('/storage/download-all');
  }

  // ===========================================================================
  // Session Files
  // ===========================================================================

  /**
   * Get all files associated with a chat session.
   */
  async getSessionFiles(
    sessionId: string
  ): Promise<ApiResponse<{ files: StorageNode[] }>> {
    return this.getData(`/storage/sessions/${sessionId}/files`);
  }

  /**
   * Get summary of files associated with a chat session.
   * Useful for delete confirmation dialogs.
   */
  async getSessionFilesSummary(
    sessionId: string
  ): Promise<ApiResponse<SessionFilesSummary>> {
    return this.getData(`/storage/sessions/${sessionId}/files/summary`);
  }

  /**
   * Delete all files associated with a chat session.
   * Called when user opts to delete files along with a chat session.
   */
  async deleteSessionFiles(
    sessionId: string,
    permanent = true
  ): Promise<ApiResponse<{ deleted_count: number }>> {
    return this.deleteData(`/storage/sessions/${sessionId}/files`, {
      permanent,
    });
  }

  // ===========================================================================
  // Connected Providers (Google Drive)
  // ===========================================================================

  /**
   * List connected external storage providers.
   */
  async listConnectedProviders(): Promise<
    ApiResponse<{ providers: ConnectedProvider[] }>
  > {
    return this.getData('/storage/providers');
  }

  /**
   * Get Google OAuth authorization URL.
   */
  async getGoogleAuthUrl(): Promise<
    ApiResponse<{ auth_url: string; state: string }>
  > {
    return this.getData('/storage/providers/google/auth-url');
  }

  /**
   * Complete Google OAuth callback.
   */
  async completeGoogleOAuth(
    code: string,
    state: string
  ): Promise<ApiResponse<ConnectedProvider>> {
    return this.postData('/storage/providers/google/callback', { code, state });
  }

  /**
   * Disconnect Google Drive.
   */
  async disconnectGoogleDrive(): Promise<
    ApiResponse<{ disconnected: boolean }>
  > {
    return this.deleteData('/storage/providers/google', null);
  }

  /**
   * List files in Google Drive.
   */
  async listGoogleDriveFiles(
    folderId?: string | null,
    page = 1,
    limit = 50
  ): Promise<
    ApiResponse<{ nodes: StorageNode[]; total: number; has_more: boolean }>
  > {
    const params = new URLSearchParams();
    if (folderId) {
      params.set('folder_id', folderId);
    }
    params.set('page', String(page));
    params.set('limit', String(limit));

    return this.getData(`/storage/providers/google/files?${params.toString()}`);
  }

  /**
   * Import files/folders selected via the Google Picker.
   *
   * Used under the `drive.file` OAuth scope: only files explicitly opened by
   * the user via the Picker are accessible to Lexomat. The backend persists
   * each picked item as a `storage_nodes` row (idempotent on
   * (provider='google_drive', external_id=file_id)) and, when
   * `expand_folders=true`, recursively enumerates folder children via the
   * Drive API.
   *
   * See `docs/drive-migration/contracts.md` Contract 6.
   */
  async importFromGoogleDrivePicker(
    request: PickerImportRequest
  ): Promise<ApiResponse<PickerImportResponse>> {
    return this.postData('/storage/providers/google/picker/import', request);
  }

  /**
   * Fetch a short-lived OAuth access token for the user's connected Drive
   * to hand to the Google Picker SDK (`oauthToken` prop on `<DrivePicker>`).
   *
   * The backend auto-refreshes via the stored refresh token; a revoked
   * refresh token surfaces here as a 403 and the caller should prompt
   * the user to reconnect Drive.
   */
  async getGoogleDrivePickerToken(): Promise<
    ApiResponse<{ access_token: string }>
  > {
    return this.getData('/storage/providers/google/picker/token');
  }

  // ===========================================================================
  // Whole-storage indexing (Spisy memory backfill, #6319)
  // ===========================================================================

  /**
   * How many of the caller's storage files still await indexing — drives the
   * "Index my files" affordance and backfill progress.
   */
  async getIndexStatus(): Promise<ApiResponse<StorageIndexStatus>> {
    return this.getData('/storage/index/status');
  }

  /**
   * Enqueue indexing for up to `limit` not-yet-attempted file-nodes so the
   * `whole_storage` context chip can answer over them. Bounded + idempotent;
   * paginate by re-calling until the returned `remaining` is 0.
   */
  async backfillIndex(
    limit = 200
  ): Promise<ApiResponse<StorageBackfillResult>> {
    return this.postData('/storage/index/backfill', { limit });
  }
}

// Export singleton instance
const storageService = StorageService.getInstance();
export default storageService;

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Format file size in human-readable format.
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${Number.parseFloat((bytes / k ** i).toFixed(2))} ${units[i]}`;
}

/**
 * Check if a node is previewable.
 */
export function isPreviewable(node: StorageNode): boolean {
  if (node.type === 'folder') return false;
  if (!node.content_type) return false;

  return (
    node.content_type.startsWith('image/') ||
    node.content_type === 'application/pdf' ||
    node.content_type.startsWith('text/')
  );
}
